import React, { useState, useEffect } from 'react';
import { 
    Video, Upload, Trash2, CheckCircle, XCircle, 
    Star, Loader2, Plus, ExternalLink, Music2 
} from 'lucide-react';
import { socialService } from '../../services/SocialService';
import { SocialPost } from '../../types/social';
import { useToast } from '../../context/ToastContext';

const AdminSocialPage: React.FC = () => {
    const [posts, setPosts] = useState<SocialPost[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [isUploading, setIsUploading] = useState(false);
    const { showToast } = useToast();

    // Form state for new post
    const [newPost, setNewPost] = useState({
        caption: '',
        is_featured: false
    });
    const [selectedFile, setSelectedFile] = useState<File | null>(null);
    const [tiktokUrl, setTiktokUrl] = useState('');
    const [isExtracting, setIsExtracting] = useState(false);

    useEffect(() => {
        loadPosts();
    }, []);

    const loadPosts = async () => {
        setIsLoading(true);
        try {
            const data = await socialService.getAllPosts();
            setPosts(data);
        } catch (error) {
            console.error('Error loading posts:', error);
            showToast('Error al cargar los posts sociales', 'error');
        } finally {
            setIsLoading(false);
        }
    };

    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.files && e.target.files[0]) {
            setSelectedFile(e.target.files[0]);
        }
    };

    const handleCreatePost = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!selectedFile) {
            showToast('Por favor selecciona un video MP4', 'error');
            return;
        }

        setIsUploading(true);
        try {
            // 1. Upload video to Storage
            const videoUrl = await socialService.uploadVideo(selectedFile);

            // 2. Create DB entry
            await socialService.createPost({
                video_url: videoUrl,
                caption: newPost.caption,
                is_featured: newPost.is_featured,
                is_active: true
            });

            showToast('Post social creado con éxito', 'success');
            setNewPost({ caption: '', is_featured: false });
            setSelectedFile(null);
            loadPosts();
        } catch (error) {
            console.error('Error creating post:', error);
            showToast('Error al crear el post social', 'error');
        } finally {
            setIsUploading(false);
        }
    };

    const handleExtractTikTok = async () => {
        if (!tiktokUrl) {
            showToast('Pega un enlace de TikTok primero', 'error');
            return;
        }

        setIsExtracting(true);
        try {
            const data = await socialService.extractTikTokVideo(tiktokUrl);
            
            // Creamos el post en la DB con los datos extraídos
            await socialService.createPost({
                video_url: data.url,
                caption: data.caption || 'Video de TikTok',
                is_featured: false,
                is_active: true
            });

            showToast('Video de TikTok extraído y publicado', 'success');
            setTiktokUrl('');
            loadPosts();
        } catch (error) {
            console.error('Error extracting TikTok:', error);
            showToast('Error al extraer el video. Asegúrate que el link sea válido.', 'error');
        } finally {
            setIsExtracting(false);
        }
    };

    const handleToggleFeatured = async (id: string, current: boolean) => {
        try {
            await socialService.updatePost(id, { is_featured: !current });
            showToast(`Post ${!current ? 'destacado' : 'quitado de destacados'}`, 'success');
            loadPosts();
        } catch (error) {
            showToast('Error al actualizar el post', 'error');
        }
    };

    const handleToggleActive = async (id: string, current: boolean) => {
        try {
            await socialService.updatePost(id, { is_active: !current });
            showToast(`Post ${!current ? 'activado' : 'desactivado'}`, 'success');
            loadPosts();
        } catch (error) {
            showToast('Error al actualizar el post', 'error');
        }
    };

    const handleDelete = async (id: string) => {
        if (!window.confirm('¿Eliminar este post permanentemente?')) return;
        try {
            await socialService.deletePost(id);
            showToast('Post eliminado', 'success');
            loadPosts();
        } catch (error) {
            showToast('Error al eliminar el post', 'error');
        }
    };

    if (isLoading) {
        return (
            <div className="flex items-center justify-center py-20">
                <Loader2 className="h-8 w-8 text-white animate-spin" />
            </div>
        );
    }

    return (
        <div className="space-y-8 pb-20">
            {/* Header */}
            <div className="bg-zinc-900/50 p-6 border border-zinc-800 rounded-sm">
                <h2 className="text-xl font-display font-black uppercase italic text-white flex items-center gap-3">
                    <Video className="text-[#1aaaff]" /> Gestión Social (Plan B)
                </h2>
                <p className="text-zinc-500 text-xs font-mono uppercase tracking-widest mt-1">
                    Hosting Nativo de videos MP4 para máxima calidad
                </p>
            </div>

            {/* Create New Post Form */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                {/* Manual Upload */}
                <div className="bg-zinc-900 border border-zinc-800 p-6 rounded-sm">
                    <h3 className="text-sm font-bold text-white uppercase tracking-widest mb-6 flex items-center gap-2">
                        <Plus size={16} /> Subida Manual (MP4)
                    </h3>
                    <form onSubmit={handleCreatePost} className="space-y-4">
                        <div>
                            <label className="block text-[10px] font-mono text-zinc-500 uppercase mb-2">Archivo de Video</label>
                            <input 
                                type="file" 
                                accept="video/mp4"
                                onChange={handleFileChange}
                                className="w-full bg-black border border-zinc-800 p-3 text-xs text-zinc-400 file:bg-zinc-800 file:text-white file:border-none file:px-4 file:py-1 file:mr-4 file:cursor-pointer hover:file:bg-zinc-700"
                            />
                        </div>
                        <div>
                            <label className="block text-[10px] font-mono text-zinc-500 uppercase mb-2">Descripción</label>
                            <textarea 
                                value={newPost.caption}
                                onChange={(e) => setNewPost({...newPost, caption: e.target.value})}
                                placeholder="Escribe algo sobre este video..."
                                className="w-full bg-black border border-zinc-800 p-3 text-sm text-white outline-none focus:border-white h-24"
                            />
                        </div>
                        <div className="flex items-center gap-3 bg-black/40 p-4 border border-zinc-800/50">
                            <input 
                                type="checkbox" 
                                id="is_featured"
                                checked={newPost.is_featured}
                                onChange={(e) => setNewPost({...newPost, is_featured: e.target.checked})}
                                className="w-4 h-4 accent-[#1aaaff]"
                            />
                            <label htmlFor="is_featured" className="text-xs font-bold text-white uppercase cursor-pointer">
                                Destacar en Landing
                            </label>
                        </div>
                        <button 
                            type="submit"
                            disabled={isUploading}
                            className="w-full bg-white text-black font-black uppercase text-xs tracking-[0.2em] py-4 hover:bg-zinc-200 transition-all flex items-center justify-center gap-2 disabled:opacity-50"
                        >
                            {isUploading ? <><Loader2 className="animate-spin" size={16} /> Subiendo...</> : <><Upload size={16} /> Publicar</>}
                        </button>
                    </form>
                </div>

                {/* TikTok URL Extraction */}
                <div className="bg-zinc-900 border border-[#1aaaff]/20 p-6 rounded-sm relative overflow-hidden">
                    <div className="absolute top-0 right-0 p-4 opacity-10">
                        <Music2 size={80} />
                    </div>
                    <h3 className="text-sm font-bold text-white uppercase tracking-widest mb-6 flex items-center gap-2">
                        <Music2 size={16} className="text-[#1aaaff]" /> Extraer desde TikTok
                    </h3>
                    <div className="space-y-6">
                        <p className="text-zinc-500 text-xs leading-relaxed">
                            Pega el enlace de un video de TikTok y nosotros extraeremos la versión limpia (sin marca de agua) y la guardaremos en tu almacenamiento.
                        </p>
                        <div>
                            <label className="block text-[10px] font-mono text-zinc-500 uppercase mb-2">URL de TikTok</label>
                            <input 
                                type="text" 
                                value={tiktokUrl}
                                onChange={(e) => setTiktokUrl(e.target.value)}
                                placeholder="https://www.tiktok.com/@usuario/video/..."
                                className="w-full bg-black border border-zinc-800 p-4 text-sm text-white outline-none focus:border-[#1aaaff]"
                            />
                        </div>
                        <button 
                            onClick={handleExtractTikTok}
                            disabled={isExtracting}
                            className="w-full bg-[#1aaaff] text-black font-black uppercase text-xs tracking-[0.2em] py-4 hover:bg-[#1aaaff]/80 transition-all flex items-center justify-center gap-2 disabled:opacity-50"
                        >
                            {isExtracting ? <><Loader2 className="animate-spin" size={16} /> Extrayendo...</> : <><ExternalLink size={16} /> Extraer y Publicar</>}
                        </button>
                        <div className="flex items-center gap-2 text-[10px] text-zinc-600 font-mono italic">
                            <CheckCircle size={12} className="text-green-500" />
                            Hosting nativo automático activado
                        </div>
                    </div>
                </div>
            </div>

            {/* List of Posts */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                {posts.map((post) => (
                    <div key={post.id} className="bg-zinc-900 border border-zinc-800 group overflow-hidden">
                        {/* Video Preview */}
                        <div className="aspect-[9/16] bg-black relative">
                            <video 
                                src={post.video_url} 
                                className="w-full h-full object-cover opacity-60 group-hover:opacity-100 transition-opacity"
                                muted
                                onMouseEnter={(e) => e.currentTarget.play()}
                                onMouseLeave={(e) => { e.currentTarget.pause(); e.currentTarget.currentTime = 0; }}
                            />
                            {post.is_featured && (
                                <div className="absolute top-2 right-2 bg-[#1aaaff] text-black p-1">
                                    <Star size={14} className="fill-black" />
                                </div>
                            )}
                        </div>

                        {/* Actions */}
                        <div className="p-4 space-y-3">
                            <p className="text-[10px] text-zinc-400 line-clamp-2 italic">
                                {post.caption || 'Sin descripción'}
                            </p>
                            <div className="flex items-center justify-between pt-2 border-t border-zinc-800">
                                <div className="flex gap-2">
                                    <button 
                                        onClick={() => handleToggleFeatured(post.id, post.is_featured)}
                                        className={`p-2 rounded-sm transition-all ${post.is_featured ? 'bg-[#1aaaff]/20 text-[#1aaaff]' : 'bg-zinc-800 text-zinc-500'}`}
                                        title="Destacar"
                                    >
                                        <Star size={14} className={post.is_featured ? 'fill-[#1aaaff]' : ''} />
                                    </button>
                                    <button 
                                        onClick={() => handleToggleActive(post.id, post.is_active)}
                                        className={`p-2 rounded-sm transition-all ${post.is_active ? 'bg-green-500/10 text-green-400' : 'bg-red-500/10 text-red-400'}`}
                                        title={post.is_active ? 'Desactivar' : 'Activar'}
                                    >
                                        {post.is_active ? <CheckCircle size={14} /> : <XCircle size={14} />}
                                    </button>
                                </div>
                                <div className="flex gap-2">
                                    <a href={post.video_url} target="_blank" rel="noreferrer" className="p-2 bg-zinc-800 text-zinc-400 hover:text-white rounded-sm">
                                        <ExternalLink size={14} />
                                    </a>
                                    <button 
                                        onClick={() => handleDelete(post.id)}
                                        className="p-2 bg-zinc-800 text-zinc-500 hover:bg-red-500 hover:text-white rounded-sm transition-all"
                                    >
                                        <Trash2 size={14} />
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
};

export default AdminSocialPage;
